/**
 * Namespaced Storage Service
 * 
 * Centralized persistence layer with intelligent namespacing, TTL support,
 * and automatic migration from legacy localStorage keys. Implements the pattern:
 * terrarium:${env}:${metaVersion}:${community}:${keyName}
 * 
 * Phase 4 implementation - advanced persistence with complete storage management
 */

import { getStoredMetaVersion } from './metaVersion';
import { detectCommunityContext, type CommunityContext } from './communityAwareCacheInvalidation';

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';
const currentEnv = isDevelopment ? 'dev' : 'prod';

// Storage configuration
const STORAGE_PREFIX = 'terrarium';
const DEFAULT_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const MIGRATION_MARKER_KEY = `${STORAGE_PREFIX}:migration_completed`;

/**
 * Interface for stored data with metadata
 */
interface StoredDataWithMetadata<T = any> {
  data: T;
  timestamp: number;
  ttl?: number;
  metaVersion?: string;
  namespace: string;
  version: number; // Schema version for future migrations
}

/**
 * Storage namespace configuration
 */
export interface StorageNamespace {
  env: string;
  metaVersion?: string;
  community?: string;
  global?: boolean; // If true, not tied to specific community
}

/**
 * Storage operation options
 */
export interface StorageOptions {
  ttl?: number; // Time to live in milliseconds
  namespace?: Partial<StorageNamespace>;
  compress?: boolean; // For future compression support
  encrypt?: boolean; // For future encryption support
}

/**
 * Storage operation result
 */
export interface StorageResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  expired?: boolean;
  namespace?: string;
}

/**
 * Legacy key mapping for migration
 * Maps old localStorage keys to new namespaced structure
 */
const LEGACY_KEY_MAPPINGS: Record<string, { newKey: string; global: boolean }> = {
  'terrarium_meta_version': { newKey: 'meta_version', global: true },
  'terrarium_db_boot_time': { newKey: 'db_boot_time', global: true },
  'terrarium_community_branding_v4': { newKey: 'community_branding', global: false },
  'terrarium_critical_branding_v4': { newKey: 'critical_branding', global: true },
  'terrarium_community_member_onboarding': { newKey: 'member_onboarding', global: false },
  'terrarium_auth_state': { newKey: 'auth_state', global: true },
  'terrarium_user_preferences': { newKey: 'user_preferences', global: true },
  'terrarium_theme_preference': { newKey: 'theme_preference', global: true },
};

/**
 * Build namespaced storage key
 */
function buildNamespacedKey(
  keyName: string, 
  namespace: StorageNamespace = {}
): string {
  const {
    env = currentEnv,
    metaVersion = getStoredMetaVersion() || 'unknown',
    community,
    global = false
  } = namespace;

  if (global) {
    // Global keys don't include community context
    return `${STORAGE_PREFIX}:${env}:${metaVersion}:global:${keyName}`;
  }

  // Community-specific keys
  const communityId = community || detectCommunityContext().hostname || 'default';
  return `${STORAGE_PREFIX}:${env}:${metaVersion}:${communityId}:${keyName}`;
}

/**
 * Parse namespaced key to extract components
 */
function parseNamespacedKey(namespacedKey: string): {
  prefix: string;
  env: string;
  metaVersion: string;
  community: string;
  keyName: string;
  isGlobal: boolean;
} | null {
  const parts = namespacedKey.split(':');
  if (parts.length !== 5 || parts[0] !== STORAGE_PREFIX) {
    return null;
  }

  return {
    prefix: parts[0],
    env: parts[1],
    metaVersion: parts[2],
    community: parts[3],
    keyName: parts[4],
    isGlobal: parts[3] === 'global'
  };
}

/**
 * Check if stored data has expired based on TTL
 */
function isExpired(storedData: StoredDataWithMetadata): boolean {
  if (!storedData.ttl) {
    return false; // No TTL means never expires
  }

  const age = Date.now() - storedData.timestamp;
  return age > storedData.ttl;
}

/**
 * Create storage metadata wrapper
 */
function createStoredDataWithMetadata<T>(
  data: T,
  options: StorageOptions = {},
  namespace: string
): StoredDataWithMetadata<T> {
  return {
    data,
    timestamp: Date.now(),
    ttl: options.ttl,
    metaVersion: getStoredMetaVersion() || undefined,
    namespace,
    version: 1, // Current schema version
  };
}

/**
 * Set data in namespaced storage
 */
export function setNamespacedStorage<T>(
  keyName: string,
  data: T,
  options: StorageOptions = {}
): StorageResult<T> {
  try {
    const namespace: StorageNamespace = {
      env: currentEnv,
      metaVersion: getStoredMetaVersion() || undefined,
      ...options.namespace
    };

    const namespacedKey = buildNamespacedKey(keyName, namespace);
    const storedData = createStoredDataWithMetadata(data, options, namespacedKey);
    
    localStorage.setItem(namespacedKey, JSON.stringify(storedData));

    if (isDevelopment) {
      console.log('NamespacedStorage: Set', {
        keyName,
        namespacedKey,
        dataType: typeof data,
        ttl: options.ttl,
      });
    }

    return {
      success: true,
      data,
      namespace: namespacedKey,
    };
  } catch (error) {
    const errorMsg = `Failed to set namespaced storage for ${keyName}: ${error}`;
    console.error('NamespacedStorage:', errorMsg);
    return {
      success: false,
      error: errorMsg,
    };
  }
}

/**
 * Get data from namespaced storage
 */
export function getNamespacedStorage<T>(
  keyName: string,
  options: StorageOptions = {}
): StorageResult<T> {
  try {
    const namespace: StorageNamespace = {
      env: currentEnv,
      metaVersion: getStoredMetaVersion() || undefined,
      ...options.namespace
    };

    const namespacedKey = buildNamespacedKey(keyName, namespace);
    const rawData = localStorage.getItem(namespacedKey);

    if (!rawData) {
      return {
        success: false,
        error: 'Key not found',
      };
    }

    const storedData: StoredDataWithMetadata<T> = JSON.parse(rawData);

    // Check for expiration
    if (isExpired(storedData)) {
      localStorage.removeItem(namespacedKey);
      
      if (isDevelopment) {
        console.log('NamespacedStorage: Expired data removed', {
          keyName,
          age: Date.now() - storedData.timestamp,
          ttl: storedData.ttl,
        });
      }

      return {
        success: false,
        expired: true,
        error: 'Data expired',
      };
    }

    if (isDevelopment) {
      const age = Date.now() - storedData.timestamp;
      console.log('NamespacedStorage: Get', {
        keyName,
        namespacedKey,
        age,
        remainingTtl: storedData.ttl ? storedData.ttl - age : 'unlimited',
      });
    }

    return {
      success: true,
      data: storedData.data,
      namespace: namespacedKey,
    };
  } catch (error) {
    const errorMsg = `Failed to get namespaced storage for ${keyName}: ${error}`;
    console.error('NamespacedStorage:', errorMsg);
    return {
      success: false,
      error: errorMsg,
    };
  }
}

/**
 * Remove data from namespaced storage
 */
export function removeNamespacedStorage(
  keyName: string,
  options: StorageOptions = {}
): StorageResult<void> {
  try {
    const namespace: StorageNamespace = {
      env: currentEnv,
      metaVersion: getStoredMetaVersion() || undefined,
      ...options.namespace
    };

    const namespacedKey = buildNamespacedKey(keyName, namespace);
    localStorage.removeItem(namespacedKey);

    if (isDevelopment) {
      console.log('NamespacedStorage: Removed', { keyName, namespacedKey });
    }

    return {
      success: true,
      namespace: namespacedKey,
    };
  } catch (error) {
    const errorMsg = `Failed to remove namespaced storage for ${keyName}: ${error}`;
    console.error('NamespacedStorage:', errorMsg);
    return {
      success: false,
      error: errorMsg,
    };
  }
}

/**
 * Clear storage by namespace pattern
 */
export function clearNamespaceStorage(
  namespace: Partial<StorageNamespace> = {}
): StorageResult<string[]> {
  try {
    const clearedKeys: string[] = [];
    const allKeys = Object.keys(localStorage);

    // Build partial pattern for matching
    const targetEnv = namespace.env || currentEnv;
    const targetMetaVersion = namespace.metaVersion || getStoredMetaVersion() || 'unknown';
    const targetCommunity = namespace.community;
    const isGlobal = namespace.global;

    allKeys.forEach(key => {
      const parsed = parseNamespacedKey(key);
      if (!parsed) return;

      // Match environment
      if (parsed.env !== targetEnv) return;

      // Match meta-version if specified
      if (namespace.metaVersion && parsed.metaVersion !== targetMetaVersion) return;

      // Match community context if specified
      if (targetCommunity && parsed.community !== targetCommunity) return;
      if (isGlobal !== undefined && parsed.isGlobal !== isGlobal) return;

      localStorage.removeItem(key);
      clearedKeys.push(key);
    });

    if (isDevelopment) {
      console.log('NamespacedStorage: Cleared namespace', {
        namespace,
        keysCleared: clearedKeys.length,
        keys: clearedKeys,
      });
    }

    return {
      success: true,
      data: clearedKeys,
    };
  } catch (error) {
    const errorMsg = `Failed to clear namespace storage: ${error}`;
    console.error('NamespacedStorage:', errorMsg);
    return {
      success: false,
      error: errorMsg,
    };
  }
}

/**
 * Get all keys matching namespace pattern
 */
export function getNamespaceKeys(
  namespace: Partial<StorageNamespace> = {}
): StorageResult<string[]> {
  try {
    const matchingKeys: string[] = [];
    const allKeys = Object.keys(localStorage);

    const targetEnv = namespace.env || currentEnv;
    const targetMetaVersion = namespace.metaVersion;
    const targetCommunity = namespace.community;
    const isGlobal = namespace.global;

    allKeys.forEach(key => {
      const parsed = parseNamespacedKey(key);
      if (!parsed) return;

      // Match criteria
      if (parsed.env !== targetEnv) return;
      if (targetMetaVersion && parsed.metaVersion !== targetMetaVersion) return;
      if (targetCommunity && parsed.community !== targetCommunity) return;
      if (isGlobal !== undefined && parsed.isGlobal !== isGlobal) return;

      matchingKeys.push(key);
    });

    return {
      success: true,
      data: matchingKeys,
    };
  } catch (error) {
    const errorMsg = `Failed to get namespace keys: ${error}`;
    console.error('NamespacedStorage:', errorMsg);
    return {
      success: false,
      error: errorMsg,
    };
  }
}

/**
 * Check if migration from legacy keys is needed
 */
export function needsMigration(): boolean {
  try {
    // Check if migration marker exists
    const migrationCompleted = localStorage.getItem(MIGRATION_MARKER_KEY);
    if (migrationCompleted) {
      return false;
    }

    // Check if any legacy keys exist
    const legacyKeys = Object.keys(LEGACY_KEY_MAPPINGS);
    return legacyKeys.some(key => localStorage.getItem(key) !== null);
  } catch (error) {
    console.error('NamespacedStorage: Failed to check migration need:', error);
    return false;
  }
}

/**
 * Migrate legacy localStorage keys to namespaced storage
 */
export async function migrateLegacyKeys(): Promise<StorageResult<{
  migrated: string[];
  errors: string[];
}>> {
  const result = {
    migrated: [] as string[],
    errors: [] as string[],
  };

  try {
    if (!needsMigration()) {
      return {
        success: true,
        data: { migrated: [], errors: ['No migration needed'] },
      };
    }

    const currentCommunity = detectCommunityContext();

    for (const [legacyKey, mapping] of Object.entries(LEGACY_KEY_MAPPINGS)) {
      try {
        const legacyValue = localStorage.getItem(legacyKey);
        if (legacyValue === null) continue;

        // Parse legacy data
        let parsedData;
        try {
          parsedData = JSON.parse(legacyValue);
        } catch {
          parsedData = legacyValue; // Store as string if not JSON
        }

        // Set in new namespaced format
        const migrationResult = setNamespacedStorage(
          mapping.newKey,
          parsedData,
          {
            ttl: DEFAULT_TTL,
            namespace: {
              global: mapping.global,
              community: mapping.global ? undefined : currentCommunity.hostname,
            }
          }
        );

        if (migrationResult.success) {
          // Remove legacy key after successful migration
          localStorage.removeItem(legacyKey);
          result.migrated.push(legacyKey);
        } else {
          result.errors.push(`Failed to migrate ${legacyKey}: ${migrationResult.error}`);
        }
      } catch (error) {
        result.errors.push(`Migration error for ${legacyKey}: ${error}`);
      }
    }

    // Set migration completion marker
    localStorage.setItem(MIGRATION_MARKER_KEY, JSON.stringify({
      completed: true,
      timestamp: Date.now(),
      version: 1,
    }));

    if (isDevelopment) {
      console.log('NamespacedStorage: Migration completed', result);
    }

    return {
      success: result.errors.length === 0,
      data: result,
    };
  } catch (error) {
    const errorMsg = `Migration failed: ${error}`;
    result.errors.push(errorMsg);
    console.error('NamespacedStorage:', errorMsg);
    return {
      success: false,
      data: result,
    };
  }
}

/**
 * Development utility to inspect namespaced storage
 */
export function inspectNamespacedStorage(): void {
  if (!isDevelopment) {
    console.warn('NamespacedStorage: inspect only available in development');
    return;
  }

  const allKeys = Object.keys(localStorage);
  const namespacedKeys = allKeys.filter(key => key.startsWith(STORAGE_PREFIX));
  const legacyKeys = allKeys.filter(key => 
    key.startsWith('terrarium_') && !key.startsWith(`${STORAGE_PREFIX}:`)
  );

  console.log('NamespacedStorage: Storage inspection');
  console.log(`- Total keys: ${allKeys.length}`);
  console.log(`- Namespaced keys: ${namespacedKeys.length}`);
  console.log(`- Legacy keys: ${legacyKeys.length}`);
  console.log(`- Migration needed: ${needsMigration()}`);

  if (namespacedKeys.length > 0) {
    console.log('- Namespaced keys structure:');
    namespacedKeys.forEach(key => {
      const parsed = parseNamespacedKey(key);
      if (parsed) {
        console.log(`  ${key} -> ${JSON.stringify(parsed)}`);
      }
    });
  }

  if (legacyKeys.length > 0) {
    console.log('- Legacy keys:', legacyKeys);
  }
}